import { useId } from "react";
import { useTranslation } from "react-i18next";
import { MdImage } from "react-icons/md";
import type { ScenariaPlan } from "src/model/model";
import type { IMedia } from "src/model/unityObject";

interface DecorStatusRowProps {
  plan?: ScenariaPlan;
  /** Hydrated conversation media — carries the s3 urls for the thumbnail. */
  contextImages: IMedia[];
  /** Disables every action while a decor-change request is in flight. */
  busy?: boolean;
  /** Toggle ON (no image yet) — opens the reference picker.
   * Omit (with the other handlers) to render the row informative-only. */
  onPick?: () => void;
  /** Toggle OFF (image integrated) — caller owns the confirm modal. */
  onRemove?: (media: IMedia) => void;
  /** Opens the full-size viewer (Options provenance modal). */
  onView?: () => void;
  onRetryAnalysis?: () => void;
  isRetryingAnalysis?: boolean;
  /** Target provenance of the decor-realign turn in flight ("image" for an
   * add/replace, "ai" for a removal), null when idle. While set, the radios
   * show this TARGET state (the user's committed intent) with an inline
   * "Recomposition du décor…" spinner chip — the realign can take minutes and
   * a server-truth-only radio would sit on the old side the whole time,
   * reading as a dead click. Snaps back to server truth if the turn fails. */
  realigningTo?: "image" | "ai" | null;
  className?: string;
}

const ACTION_LINK = "text-purple-500 hover:text-purple-600 font-medium text-xs disabled:opacity-50";

// The affirmative decor-provenance statement: always answers "is my image
// driving the decor?" in reserved space, in one of three server-truth states —
// none (LLM-invented), integrated (thumbnail + filename), or vision-failed
// (decor composed without the image + retry). This row never shows anything
// the server hasn't persisted: a gallery pick fires the integration
// immediately (busy strip while it runs), so there is no staged limbo.
//
// Interactive rows expose the choice as a two-option radio group (same native
// radio pattern as Destination/access mode in ConfigureGenerationPanel) so the
// OR between the two provenances is visible at a glance. Picking "your image"
// with no image opens the picker; picking "AI-invented" with an image fires
// onRemove (confirm modal lives upstream). The checked state mirrors server
// truth only, so a cancelled pick or confirm leaves it honest.
// Informative-only rows (no onPick) keep the plain one-line statement.
const DecorStatusRow = ({
  plan,
  contextImages,
  busy = false,
  onPick,
  onRemove,
  onView,
  onRetryAnalysis,
  isRetryingAnalysis = false,
  realigningTo = null,
  className = "",
}: DecorStatusRowProps) => {
  const { t } = useTranslation();
  const pc = "pages.chat";
  // Several rows can be mounted at once (plan card + Blockade section): each
  // instance needs its own radio-group name or the groups would swallow each
  // other's checked state.
  const radioGroupName = `decor-provenance-${useId()}`;

  // Max 1 reference image by design (400-char trunk budget) — entry [0] is it.
  const entry = (plan?.attachedImages ?? [])[0];
  const media = entry
    ? contextImages.find((m) => Number(m.id) === Number(entry.mediaId))
    : undefined;
  const name = media?.filename || media?.name || t(`${pc}.decorRowYourImage`);

  const interactive = !!onPick;
  const hasImage = !!entry;
  // The side the radios display: mid-realign it is the user's committed
  // TARGET, otherwise persisted server truth.
  const displayHasImage = realigningTo ? realigningTo === "image" : hasImage;

  // Vision failed: the image is linked but its description could not be
  // produced — the decor was composed WITHOUT it. Never silent.
  const failedBadge = entry?.failed && (
    <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
      {t(`${pc}.decorRowAnalysisFailed`)}
    </span>
  );

  const secondaryLinks = (
    <span className="flex items-center gap-2 shrink-0">
      {entry?.failed && onRetryAnalysis && (
        <button
          type="button"
          className={ACTION_LINK}
          disabled={busy || isRetryingAnalysis}
          onClick={onRetryAnalysis}
        >
          {isRetryingAnalysis
            ? t("pages.chat.configureGeneration.imageAnalysisRetrying")
            : t("pages.chat.configureGeneration.imageAnalysisRetry")}
        </button>
      )}
      {entry && onView && (
        <button type="button" className={ACTION_LINK} disabled={busy} onClick={onView}>
          {t("pages.chat.configureGeneration.trunkImageLink")}
        </button>
      )}
      {entry && interactive && (
        <button type="button" className={ACTION_LINK} disabled={busy} onClick={onPick}>
          {t(`${pc}.decorRowChange`)}
        </button>
      )}
    </span>
  );

  if (interactive) {
    // Reverting to "AI-invented" needs a removable media (the confirm modal
    // takes it); until the media is hydrated that radio is honest but inert.
    const aiRadioDisabled = busy || (hasImage && !(media && onRemove));
    return (
      <div className={className}>
        <label className="flex w-fit items-center gap-2 mb-1 cursor-pointer">
          <input
            type="radio"
            name={radioGroupName}
            checked={!displayHasImage}
            onChange={() => {
              if (media && onRemove) onRemove(media);
            }}
            disabled={aiRadioDisabled}
            className="h-3.5 w-3.5 cursor-pointer accent-green-600"
          />
          <span className="text-sm text-gray-700">{t(`${pc}.decorRowNone`)}</span>
        </label>
        {/* The image details live NEXT TO the label, not inside it: nested
            buttons inside a <label> would race the radio's activation. */}
        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
          <label className="flex w-fit items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name={radioGroupName}
              checked={displayHasImage}
              onChange={() => {
                if (!hasImage) onPick();
              }}
              disabled={busy}
              className="h-3.5 w-3.5 cursor-pointer accent-green-600"
            />
            <span className="text-sm text-gray-700">{t(`${pc}.decorRowNoneAction`)}</span>
          </label>
          {/* Mid-realign the details are HIDDEN, not just disabled: they still
              describe the OLD server state (a removal-in-flight would show the
              image being removed next to an unchecked radio) and the radios
              already display the target. Only the spinner chip rides along;
              thumbnail/name/links come back with the landed server truth. */}
          {entry && !realigningTo && (
            <span className="flex items-center gap-2 min-w-0">
              {media && (
                <img
                  src={media.thumbnail_s3_url || media.s3_url}
                  alt={name}
                  className={`h-8 w-8 rounded object-cover border border-purple-200 shrink-0 ${
                    onView ? "cursor-pointer" : ""
                  }`}
                  onClick={onView}
                />
              )}
              <span className="text-sm font-medium text-purple-800 break-all min-w-0">{name}</span>
            </span>
          )}
          {/* The realign runs for MINUTES; while it does, this chip is the
              only thing telling the user the frozen radios are intentional. */}
          {realigningTo && (
            <span className="flex items-center gap-1.5 text-xs text-purple-600">
              <span className="inline-block w-3 h-3 border-2 border-purple-200 border-t-purple-500 rounded-full animate-spin" />
              {t(`${pc}.decorRealignInProgress`)}
            </span>
          )}
          {!realigningTo && failedBadge}
          {!realigningTo && secondaryLinks}
        </div>
      </div>
    );
  }

  // Informative-only (no handlers): the plain one-line provenance statement.
  return (
    <div className={`flex flex-wrap items-center gap-x-2.5 gap-y-1 ${className}`}>
      {entry && media ? (
        <img
          src={media.thumbnail_s3_url || media.s3_url}
          alt={name}
          className={`h-8 w-8 rounded object-cover border border-purple-200 shrink-0 ${
            onView ? "cursor-pointer" : ""
          }`}
          onClick={onView}
        />
      ) : (
        <span className="h-8 w-8 rounded border border-dashed border-gray-300 flex items-center justify-center text-gray-400 shrink-0">
          <MdImage size={14} />
        </span>
      )}

      {entry ? (
        <span className="text-sm text-gray-700 min-w-0">
          {t(`${pc}.decorRowIntegrated`)}{" "}
          <span className="font-medium text-purple-800 break-all">{name}</span>
        </span>
      ) : (
        <span className="text-sm text-gray-600">{t(`${pc}.decorRowNone`)}</span>
      )}

      {failedBadge}
      {secondaryLinks}
    </div>
  );
};

export default DecorStatusRow;
